import * as React from 'react'

import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { Button } from '~/client/src/shared/components/Button/Button'
import CompactSuperFilter from '~/client/src/shared/components/Filters/CompactSuperFilter'
import SuperFilterHandleButton from '~/client/src/shared/components/Filters/SuperFilter/SuperFilterHandleButton'
import PermitTypeIcon from '~/client/src/shared/components/PermitTypeIcon/PermitTypeIcon'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import { buildFilterTypeIconMap } from '~/client/src/shared/constants/FilterTypeMappings'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import {
  getLogisticsFilterTypeCaption,
  LogisticsFilterType,
} from '~/client/src/shared/enums/LogisticsFilterType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'
import BaseLogisticsFilterStore from '~/client/src/shared/stores/BaseLogisticsFilter.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import './CompactLogisticsFilter.scss'

const clearXFilters = 'Clear {x} filters'

interface IProps {
  store: LogisticsFilterStore
  onClose: () => void
  onApply?: () => void
  isShown: boolean
  selectedOptionsCount: number
  companiesStore: CompaniesStore
  projectMembersStore: ProjectMembersStore
  tagsStore: TagsStore
}

const UNKNOWN_USER_INITIALS = 'UU'

const STORE_TO_ICON_MAP = buildFilterTypeIconMap({
  size: 16,
  className: 'tw:shrink-0 tw:mr-2.5',
})

const locationFilterTypes: string[] = [
  LogisticsFilterType.Location,
  LogisticsFilterType.Equipment,
]

@observer
export default class CompactLogisticsFilter extends React.Component<IProps> {
  @observable private isOptionsShown: boolean = false
  @observable private localProps: IProps = null
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public componentDidMount(): void {
    if (this.props.isShown) {
      this.closeOverlay = notifyOverlayOpen()
    }
  }

  public componentDidUpdate(prevProps: IProps): void {
    if (!prevProps.isShown && this.props.isShown) {
      this.closeOverlay = notifyOverlayOpen()
    } else if (prevProps.isShown && !this.props.isShown) {
      this.closeOverlay?.()
      this.closeOverlay = null
    }
  }

  public componentWillUnmount(): void {
    this.closeOverlay?.()
    this.closeOverlay = null
  }

  public render() {
    const { isShown, onClose } = this.props
    return (
      <>
        <div
          onClick={onClose}
          className={classList({
            dimmer: true,
            shown: isShown,
          })}
        />
        <div
          className={classList({
            'col filter-holder tw:pb-safe-3': true,
            shown: isShown,
          })}
        >
          {this.renderHeader()}
          {this.renderResetButton()}
          <div className="scrollable">{this.renderContent()}</div>
        </div>
      </>
    )
  }

  private renderResetButton() {
    const { selectedOptionsCount } = this.props

    if (!selectedOptionsCount || this.isOptionsShown) {
      return
    }

    return (
      <div
        className="filter-wrapper no-grow bb-light-grey bt-light-grey filter-handle-button-holder col y-center pl10 text large blue-highlight"
        onClick={this.resetFilters}
      >
        {clearXFilters.replace('{x}', selectedOptionsCount.toString())}
      </div>
    )
  }

  private renderHeader() {
    const { selectedOptionsCount, onClose } = this.props
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:p-2.5">
        {this.isOptionsShown ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.back}
            onClick={this.handleBackClick}
          >
            <ChevronLeftIcon />
          </Button>
        ) : (
          <div className="tw:size-7 tw:shrink-0" />
        )}
        <div className="tw:flex-1 tw:text-center tw:text-title-sm tw:text-foreground">
          {this.headerLabel}
        </div>
        <Button size="sm" onClick={this.applyAllFilters}>
          {`${Localization.translator.select} ${selectedOptionsCount || ''}`}
        </Button>
        {!this.isOptionsShown && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.close}
            onClick={onClose}
          >
            <XIcon />
          </Button>
        )}
      </div>
    )
  }

  private get headerLabel(): string {
    let headerLabel = Localization.translator.filters
    Object.keys(this.storesMap).find(filterType => {
      const store = this.storesMap[filterType]
      if (store?.isShown) {
        headerLabel = `${
          Localization.translator.filterBy
        } ${getLogisticsFilterTypeCaption(filterType as LogisticsFilterType)}`
        return
      }
    })

    return headerLabel
  }

  private renderContent() {
    return Object.keys(this.storesMap).map(filterType => {
      const store = this.storesMap[filterType]
      const shouldRenderPopups = this.isOptionsShown && store?.isShown
      const selectedOptionsCount = store?.selectedOptions?.size

      return (
        <React.Fragment key={filterType}>
          {!this.isOptionsShown && (
            <SuperFilterHandleButton
              filterStore={store}
              onToggle={this.onToggle.bind(this, store)}
              className="no-grow bb-light-grey filter-handle-button-holder col y-center pr10"
            >
              {STORE_TO_ICON_MAP[filterType]}
              <span
                className={classList({
                  'no-grow text extra-large bold capitalize': true,
                  'w-fit-content mr-auto': !selectedOptionsCount,
                })}
              >
                {getLogisticsFilterTypeCaption(
                  filterType as LogisticsFilterType,
                )}
              </span>
              {!!selectedOptionsCount && (
                <div className="ml10 no-grow text extra-large compact-select-button px5 brada4 w-fit-content mr-auto">
                  {selectedOptionsCount}
                </div>
              )}
              <ChevronRightIcon size={16} />
            </SuperFilterHandleButton>
          )}
          {shouldRenderPopups && this.renderPopups(store, filterType)}
        </React.Fragment>
      )
    })
  }

  private renderPopups(store: BaseLogisticsFilterStore, filterType: string) {
    return (
      <CompactSuperFilter
        filterStores={[store]}
        storeToIconMap={STORE_TO_ICON_MAP}
        getFilteredOptionsCount={this.getFilteredOptionsCount}
        shouldRenderHeader={false}
        customIconRender={this.customIconRender.bind(this, filterType)}
        optionFormatter={
          locationFilterTypes.includes(filterType) && this.formatLocation
        }
      />
    )
  }

  private formatLocation = (option: string): JSX.Element => {
    const splitOption = option.split('|')
    if (splitOption.length !== 2) {
      return <>{option}</>
    }
    // By id: the kind half names no store for a re-kinded or project-added kind (STX1-597)
    const location = this.localProps.tagsStore.getLocationByIdWithDeleted(
      splitOption[1],
    )
    const tag = location && !location.isDeleted ? location : null

    if (!tag) {
      return <>{option}</>
    }

    return (
      <div className="row no-flex-children my2 text large">
        <span key={tag.id + tag.type} className="mr4 no-grow tag-cell">
          <SitemapAttributeTag
            dataObject={tag as LocationAttributeBase}
            contentContainerClassName="text-ellipsis py2"
            shouldShowAsTag={true}
          >
            <span>{tag.name}</span>
          </SitemapAttributeTag>
        </span>
      </div>
    )
  }

  private applyAllFilters = () => {
    if (this.isOptionsShown) {
      this.handleBackClick()
    }
    this.props.onClose()
  }

  private customIconRender(filterType: LogisticsFilterType, option: string) {
    switch (filterType) {
      case LogisticsFilterType.ResponsibleContact:
        return this.avatarIcon(option)

      case LogisticsFilterType.Company:
        return this.companyIcon(option)

      case LogisticsFilterType.Type:
        return this.permitIcon(option)

      default:
        return null
    }
  }

  private permitIcon(option: string) {
    return (
      <PermitTypeIcon
        iconName={option}
        size="small"
        className="no-grow mr8 mt4"
      />
    )
  }

  private avatarIcon(option: string) {
    const user = this.localProps.projectMembersStore.getById(option)
    return (
      <Avatar
        src={user?.avatarUrl}
        initials={user?.initials || UNKNOWN_USER_INITIALS}
        size={AvatarSize.Tiny}
        className="mr8"
      />
    )
  }

  private companyIcon(option: string) {
    const company = this.localProps.companiesStore.getCompanyById(option)
    return (
      <Avatar
        src={company?.avatarUrl}
        avatarType={AvatarType.Company}
        size={AvatarSize.Tiny}
        className="mr8"
      />
    )
  }

  private resetFilters = () => {
    Object.keys(this.storesMap).map(filterType => {
      const store = this.storesMap[filterType]
      store?.clickOnDeselectAll()
      store?.clickOnApply()
    })

    this.props.onClose()
    this.props.onApply && this.props.onApply()
  }

  private handleBackClick = () => {
    Object.keys(this.storesMap).map(filterType => {
      const store = this.storesMap[filterType]
      store?.clickOnApply()
    })

    this.props.onApply && this.props.onApply()
    this.onToggle()
  }

  @action
  private onToggle = (store?: BaseLogisticsFilterStore) => {
    this.isOptionsShown = !this.isOptionsShown
    store?.toggle()
  }

  private get storesMap() {
    return this.props.store.filterStoresByTypeMap
  }

  private getFilteredOptionsCount = (itemIds: string[]): number => {
    return itemIds.length
  }
}
